import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useCurrentUser } from '../account/useAuth';
import { acknowledgeBadge, getMyBadges, type UserBadge } from './badgesApi';
import './BadgeToast.css';

/**
 * Quiet badge toast — surfaces newly-awarded badges *once*, never
 * again.
 *
 * Design rules from PLAN.md:
 *   * Never an XP / streak / Skinner-box notification.
 *   * Toasted exactly once, then quiet.
 *   * The kid can dismiss any individual toast without dismissing
 *     the rest.
 *
 * The component polls `/me/badges?unacknowledged=true` once on
 * mount and once on the auth state changing. New badges fade in;
 * dismissing them POSTs the acknowledge endpoint so they never
 * reappear in another tab.
 *
 * No auto-dismiss timer. The kid chooses when to dismiss.
 */
export function BadgeToast(): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const qc = useQueryClient();
  const unacked = useQuery({
    queryKey: ['badges', 'mine', 'unacknowledged'],
    queryFn: () => getMyBadges({ unacknowledged: true }),
    enabled: Boolean(user),
    // No interval polling — we don't want the toast to re-pop
    // every time a kid taps somewhere.
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const ack = useMutation({
    mutationFn: (code: string) => acknowledgeBadge(code),
    onMutate: async (code: string) => {
      // Optimistic update — clear the toast immediately so the kid
      // doesn't see a flash of stale state.
      const previous = qc.getQueryData<UserBadge[]>(['badges', 'mine', 'unacknowledged']);
      qc.setQueryData<UserBadge[]>(['badges', 'mine', 'unacknowledged'], (old) =>
        (old ?? []).filter((b) => b.code !== code),
      );
      return { previous };
    },
    onError: (_err, _code, context) => {
      // Roll back the optimistic clear if the network was the
      // problem.
      if (context?.previous) {
        qc.setQueryData(['badges', 'mine', 'unacknowledged'], context.previous);
      }
    },
    onSuccess: () => {
      // Refresh the full /me/badges list (the badge stays visible
      // on the portfolio, just not in the toast queue).
      qc.invalidateQueries({ queryKey: ['badges', 'mine'] });
    },
  });

  // Local "fade in then settle" state so the toast doesn't pop in
  // hard. We tick once on mount of each badge.
  const [visible, setVisible] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!unacked.data) return;
    const ids = new Set(unacked.data.map((b) => b.code));
    setVisible(ids);
  }, [unacked.data]);

  if (!user) return null;
  if (!unacked.data || unacked.data.length === 0) return null;

  // Show at most 3 toasts at a time. With more, kids get a "+N more"
  // affordance and the next ones surface as they dismiss. PLAN.md
  // is explicit: the toast is a *quiet* notification, not a wall of
  // celebration popups.
  const visibleBadges = unacked.data.filter((b) => visible.has(b.code));
  const TOAST_LIMIT = 3;
  const shown = visibleBadges.slice(0, TOAST_LIMIT);
  const hiddenCount = visibleBadges.length - shown.length;

  return (
    <div className="bl-toast-stack" role="region" aria-label="New badges">
      {shown.map((badge) => (
        <div key={badge.code} className="bl-toast" data-testid="badge-toast">
          <div className="bl-toast__icon" aria-hidden="true">
            {badge.icon}
          </div>
          <div className="bl-toast__body">
            <div className="bl-toast__name">New: {badge.name}</div>
            <div className="bl-toast__desc">{badge.description}</div>
            <div className="bl-toast__why">{badge.why_it_matters}</div>
          </div>
          <button
            type="button"
            className="bl-toast__close"
            onClick={() => ack.mutate(badge.code)}
            disabled={ack.isPending}
            aria-label={`Got it — dismiss ${badge.name}`}
          >
            Got it
          </button>
        </div>
      ))}
      {hiddenCount > 0 ? (
        <div className="bl-toast-overflow" aria-live="polite">
          + {hiddenCount} more — dismiss one to see the next
        </div>
      ) : null}
    </div>
  );
}
